import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const EMPTY_CONTACT = { name: "", email: "", company: "" };
const fail = (fallback) => (e) => toast.error(e?.response?.data?.detail || fallback);

// Recipient picking, contact management and send action for the report email dialog.
export function useSendReport({ projectId, runId, open }) {
  const { activeWorkspaceId } = useAuth();
  const [members, setMembers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [sends, setSends] = useState([]);
  const [picked, setPicked] = useState({ users: [], contacts: [] });
  const [kind, setKind] = useState("pm_report");
  const [note, setNote] = useState("");
  const [attachLetter, setAttachLetter] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(EMPTY_CONTACT);

  useEffect(() => {
    if (!open) return;
    setKind("pm_report");
    Promise.all([api.get(`/workspaces/${activeWorkspaceId}/members`), api.get(`/projects/${projectId}/contacts`), api.get(`/projects/${projectId}/report/sends`)])
      .then(([m, c, s]) => { setMembers(m.data); setContacts(c.data); setSends(s.data); });
  }, [open, projectId, activeWorkspaceId]);

  const flip = (key, id) => setPicked((p) => ({ ...p, [key]: p[key].includes(id) ? p[key].filter((x) => x !== id) : [...p[key], id] }));
  const addContact = async () => {
    if (!form.name || !form.email) return toast.error("Name and email are required");
    try { const { data } = await api.post(`/projects/${projectId}/contacts`, form); setContacts([...contacts, data]); setPicked((p) => ({ ...p, contacts: [...p.contacts, data.id] })); setForm(EMPTY_CONTACT); toast.success("Contact added"); }
    catch (e) { fail("Could not add contact")(e); }
  };
  const removeContact = async (id) => { await api.delete(`/projects/${projectId}/contacts/${id}`); setContacts(contacts.filter((c) => c.id !== id)); setPicked((p) => ({ ...p, contacts: p.contacts.filter((x) => x !== id) })); };
  const total = picked.users.length + picked.contacts.length;
  const send = async () => {
    if (!total) return toast.error("Choose at least one recipient");
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/report/send`, { kind, run_id: runId || null, recipient_user_ids: picked.users, contact_ids: picked.contacts, note, origin: window.location.origin, attach_letter: attachLetter });
      toast[data.ok ? "success" : "warning"](`${data.sent} of ${data.results.length} email${data.results.length > 1 ? "s" : ""} sent`);
      setPicked({ users: [], contacts: [] }); setNote(""); setSends([...data.results, ...sends]);
    } catch (e) { fail("Send failed")(e); } finally { setBusy(false); }
  };
  return { members, contacts, sends, picked, kind, setKind, note, setNote, attachLetter, setAttachLetter, busy, form, setForm, flip, addContact, removeContact, send, total };
}
